import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowRight, Briefcase, Search } from "lucide-react";
import UnreadBadge from "@/components/UnreadBadge";
import { formatStaleness, isStale } from "@/lib/dates";

const STATUS_STYLES = {
  submitted: "bg-amber-100 text-amber-900 border-amber-200",
  in_review: "bg-blue-100 text-blue-900 border-blue-200",
  approved: "bg-emerald-100 text-emerald-900 border-emerald-200",
  closed: "bg-gray-100 text-gray-700 border-gray-200",
};

const STATUS_FILTERS = ["all", "submitted", "in_review", "approved", "closed"];

/**
 * "All claims" CRM card — search + status pills + sortable table with
 * staleness badge + Review action.
 */
export function ClaimsTable({
  filtered,
  sortedFiltered,
  filter,
  setFilter,
  statusFilter,
  setStatusFilter,
  sortByActivity,
  setSortByActivity,
}) {
  return (
    <Card className="border-[#E5E7EB]">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <CardTitle className="font-['Outfit']">All claims</CardTitle>
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="pl-9 w-60"
                data-testid="admin-search-input"
              />
            </div>
            <div className="flex gap-1">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    statusFilter === s
                      ? "bg-[#1E3F33] text-[#F9F8F6] border-[#1E3F33]"
                      : "bg-white text-gray-700 border-[#E5E7EB] hover:border-[#1E3F33]"
                  }`}
                  data-testid={`status-filter-${s}`}
                >
                  {s.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <Briefcase className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No claims match your filter.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Policyholder</TableHead>
                <TableHead>Damage</TableHead>
                <TableHead>Zip</TableHead>
                <TableHead>Carrier</TableHead>
                <TableHead>DOL</TableHead>
                <TableHead>
                  <button
                    onClick={() => setSortByActivity((s) => !s)}
                    className="inline-flex items-center gap-1 hover:text-[#1E3F33]"
                    data-testid="sort-by-activity-toggle"
                  >
                    Last activity
                    {sortByActivity && (
                      <span className="text-[10px] uppercase tracking-widest text-[#C25934]">
                        (stale ↑)
                      </span>
                    )}
                  </button>
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedFiltered.map((c) => (
                <TableRow
                  key={c.id}
                  data-testid={`admin-claim-row-${c.id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{c.full_name}</p>
                      <UnreadBadge count={c.unread_count} claimId={c.id} />
                    </div>
                    <p className="text-xs text-gray-500">{c.email}</p>
                  </TableCell>
                  <TableCell className="capitalize">{c.damage_type}</TableCell>
                  <TableCell>{c.zip_code}</TableCell>
                  <TableCell>{c.carrier}</TableCell>
                  <TableCell>{c.date_of_loss}</TableCell>
                  <TableCell
                    className={
                      isStale(c.last_activity_at)
                        ? "text-[#C25934] font-medium"
                        : "text-gray-700"
                    }
                    data-testid={`last-activity-${c.id}`}
                  >
                    {formatStaleness(c.last_activity_at)}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`text-xs px-2.5 py-1 rounded-full border ${
                        STATUS_STYLES[c.status]
                      }`}
                    >
                      {c.status?.replace("_", " ")}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Link
                      to={`/claim/${c.id}`}
                      data-testid={`admin-open-${c.id}`}
                    >
                      <Button variant="ghost" size="sm">
                        Review <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
